import {BUYER_IDENTITY_APPROVAL_ACTION, Permission} from '@sollapay/enums';

import type {TrustAccountListMode} from '../hooks';
import type {TrustAccountPermissions} from '../providers';
import type {TrustAccountAction} from '@sollapay/enums';
import type {FirmTrustAccountListItemDTO} from '@sollapay/types';

export interface TrustAccountRowPermissions {
  canManage: boolean;
  canDelete: boolean;
}

/** The trust-account action behind each per-account flag the screens gate on. */
export const TRUST_ACCOUNT_PERMISSION_ACTIONS = {
  canUpdateAccount: Permission.TRUST_ACCOUNTS_UPDATE,
  canSignDeclarations: Permission.TRUST_ACCOUNTS_SIGN_DECLARATIONS,
  canApproveBuyerIdentityChange: BUYER_IDENTITY_APPROVAL_ACTION,
  canSubmitForReview: Permission.TRUST_ACCOUNTS_SUBMIT,
  canCreateBuyer: Permission.BUYERS_CREATE,
  canUpdateBuyer: Permission.BUYERS_UPDATE,
  canDeleteBuyer: Permission.BUYERS_DELETE,
  canCreateBeneficiary: Permission.BENEFICIARIES_CREATE,
  canUpdateBeneficiary: Permission.BENEFICIARIES_UPDATE,
  canDeleteBeneficiary: Permission.BENEFICIARIES_DELETE,
  canCollectPayments: Permission.PAYMENTS_COLLECT,
  canCreatePayout: Permission.PAYMENTS_PAYOUT,
  canResendPayments: Permission.PAYMENTS_RESEND,
  canUploadDocuments: Permission.DOCUMENTS_UPLOAD,
  canDeleteDocuments: Permission.DOCUMENTS_DELETE,
  canManageMembers: Permission.TRUST_ACCOUNTS_MANAGE_MEMBERS
} as const satisfies Record<keyof TrustAccountPermissions, TrustAccountAction>;

export const TRUST_ACCOUNT_WRITE_PERMISSIONS = Object.values(TRUST_ACCOUNT_PERMISSION_ACTIONS);

/**
 * Every per-account action flag, read from the actions the API says the caller may perform on this
 * account — the server decides, the client only renders.
 */
export function resolveTrustAccountPermissions(
  allowedActions: readonly TrustAccountAction[]
): TrustAccountPermissions {
  return Object.fromEntries(
    Object.entries(TRUST_ACCOUNT_PERMISSION_ACTIONS).map(([flag, action]) => [
      flag,
      allowedActions.includes(action)
    ])
  ) as unknown as TrustAccountPermissions;
}

/** How the caller relates to an account — the list's role tabs, in display order. */
export const TRUST_ACCOUNT_ACCESS_SECTIONS = ['trustee', 'operator', 'viewer'] as const;

export type TrustAccountAccessSection = (typeof TRUST_ACCOUNT_ACCESS_SECTIONS)[number];

/** Trustee, else operator if they can edit it, else view-only (a viewer member or a firm-wide reader). */
export function getTrustAccountAccessSection(
  account: Pick<FirmTrustAccountListItemDTO, 'isTrustee' | 'allowedActions'>
): TrustAccountAccessSection {
  if (account.isTrustee) return 'trustee';
  return account.allowedActions.includes(Permission.TRUST_ACCOUNTS_UPDATE) ? 'operator' : 'viewer';
}

/** What the list's access filter can select: everything, or one section. */
export type TrustAccountAccessFilter = 'all' | TrustAccountAccessSection;

export function countByAccessSection(
  accounts: Pick<FirmTrustAccountListItemDTO, 'isTrustee' | 'allowedActions'>[]
): Record<TrustAccountAccessSection, number> {
  const counts: Record<TrustAccountAccessSection, number> = {trustee: 0, operator: 0, viewer: 0};
  for (const account of accounts) counts[getTrustAccountAccessSection(account)] += 1;
  return counts;
}

/**
 * The filter actually applied: "all" while the filter isn't offered, and a role that has no
 * accounts (e.g. the trustee default for a pure operator) falls back to the first one that has.
 */
export function resolveAccessFilter(
  selected: TrustAccountAccessFilter,
  counts: Record<TrustAccountAccessSection, number>,
  isOffered: boolean
): TrustAccountAccessFilter {
  if (!isOffered) return 'all';
  if (selected === 'all' || counts[selected] > 0) return selected;
  return TRUST_ACCOUNT_ACCESS_SECTIONS.find(section => counts[section] > 0) ?? 'all';
}

/**
 * How the list sorts: the trustee tab shows only the caller's accounts, so it sorts as `own`; an
 * `own` caller who still sees other trustees' accounts sorts them `grouped`, by trustee.
 */
export function resolveSortMode(params: {
  accessFilter: TrustAccountAccessFilter;
  mode: TrustAccountListMode;
  hasForeignAccounts: boolean;
}): TrustAccountListMode {
  const {accessFilter, mode, hasForeignAccounts} = params;
  if (accessFilter === 'trustee') return 'own';
  return mode === 'own' && hasForeignAccounts ? 'grouped' : mode;
}

export function filterByAccessSection<
  T extends Pick<FirmTrustAccountListItemDTO, 'isTrustee' | 'allowedActions'>
>(accounts: T[], filter: TrustAccountAccessFilter): T[] {
  return filter === 'all'
    ? accounts
    : accounts.filter(account => getTrustAccountAccessSection(account) === filter);
}

export type TrustAccountBannerMode = 'guest' | 'viewer' | 'operator';

/**
 * Which role banner a trust account page shows. Guest for an outside guest; viewer when the
 * caller can write nothing here; operator by the same rule as the list's operator section (not
 * trustee, can edit the account); otherwise none — the trustee needs no reminder.
 */
export function getTrustAccountBannerMode({
  isTrustee,
  isGuest,
  permissions
}: {
  isTrustee: boolean;
  isGuest: boolean;
  permissions: TrustAccountPermissions;
}): TrustAccountBannerMode | null {
  if (isGuest) return 'guest';
  if (!Object.values(permissions).some(Boolean)) return 'viewer';
  if (!isTrustee && permissions.canUpdateAccount) return 'operator';
  return null;
}

/** Per-row write flags shared by the table and card list, read from each row's allowed actions. */
export function getTrustAccountRowPermissions(
  account: FirmTrustAccountListItemDTO
): TrustAccountRowPermissions {
  return {
    canManage: account.allowedActions.includes(Permission.TRUST_ACCOUNTS_UPDATE),
    canDelete: account.allowedActions.includes(Permission.TRUST_ACCOUNTS_DELETE)
  };
}
